import { DEFAULT_FTOL, DEFAULT_XTOL, type ScalarSolverOptions, type SolverResult } from './types';

/**
 * Bisection (yarimlash). Sekin, lekin [a, b] da ildiz bo'lsa DOIM topadi.
 * Newton uchun boshlang'ich yaqinlashish yoki ishonchli zaxira sifatida ishlatiladi.
 */
export function bisection(
  f: (x: number) => number,
  a: number,
  b: number,
  opts: ScalarSolverOptions = {},
): SolverResult {
  const xtol = opts.xtol ?? DEFAULT_XTOL;
  const ftol = opts.ftol ?? DEFAULT_FTOL;
  const maxIter = opts.maxIter ?? 200;

  let fa = f(a);
  const fb = f(b);
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) {
    return {
      converged: false,
      value: NaN,
      iterations: 0,
      residual: NaN,
      message: 'solver.nonFinite',
    };
  }
  if (fa === 0) return { converged: true, value: a, iterations: 0, residual: 0 };
  if (fb === 0) return { converged: true, value: b, iterations: 0, residual: 0 };
  if (fa * fb > 0) {
    return {
      converged: false,
      value: NaN,
      iterations: 0,
      residual: Math.min(Math.abs(fa), Math.abs(fb)),
      message: 'solver.noBracket',
    };
  }

  let lo = a;
  let hi = b;
  let mid = 0.5 * (lo + hi);
  let fm = f(mid);
  let i = 0;
  for (; i < maxIter; i++) {
    mid = 0.5 * (lo + hi);
    fm = f(mid);
    if (!Number.isFinite(fm)) {
      return {
        converged: false,
        value: mid,
        iterations: i,
        residual: NaN,
        message: 'solver.nonFinite',
      };
    }
    if (Math.abs(fm) <= ftol || 0.5 * (hi - lo) <= xtol) {
      return { converged: true, value: mid, iterations: i + 1, residual: Math.abs(fm) };
    }
    if (fa * fm < 0) {
      hi = mid;
    } else {
      lo = mid;
      fa = fm;
    }
  }
  return {
    converged: false,
    value: mid,
    iterations: i,
    residual: Math.abs(fm),
    message: 'solver.maxIterations',
  };
}
